import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { Offer } from '../types';
import { fetchOfferDocuments } from '../services/offerService';
import './MySubmissions.css';

interface SubmissionWithListing extends Offer {
  listingAddress?: string;
  listingCity?: string;
  listingState?: string;
  filesCount?: number;
}

const MySubmissions: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState<SubmissionWithListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMySubmissions = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        // Query offers where current user is the buyer's agent
        // Note: Filtering by submittedVia in code to avoid composite index requirement
        const offersQuery = query(
          collection(db, 'offers'),
          where('buyerAgentId', '==', currentUser.uid),
          orderBy('submissionDate', 'desc')
        );

        const offersSnapshot = await getDocs(offersQuery);
        
        const offersData: SubmissionWithListing[] = [];

        // Fetch listing details for each offer (filter by shared-link or email in code)
        for (const offerDoc of offersSnapshot.docs) {
          const data = offerDoc.data();
          
          // Only show submissions via shared link or email
          if (data.submittedVia !== 'shared-link' && data.submittedVia !== 'email') {
            continue;
          }
          
          const offerData = {
            id: offerDoc.id,
            ...data,
            submissionDate: data.submissionDate?.toDate()
          } as Offer;

          // Fetch offer documents to get file counts
          const offerDocuments = await fetchOfferDocuments(offerDoc.id);
          const filesCount = offerDocuments?.files?.length || data.files?.length || 0;

          // Fetch the listing details using doc reference
          if (offerData.listingId) {
            try {
              const listingDocRef = doc(db, 'listings', offerData.listingId);
              const listingSnapshot = await getDoc(listingDocRef);
              
              if (listingSnapshot.exists()) {
                const listing = listingSnapshot.data();
                offersData.push({
                  ...offerData,
                  listingAddress: listing.address,
                  listingCity: listing.city,
                  listingState: listing.state,
                  filesCount
                });
              } else {
                console.warn('Listing not found for ID:', offerData.listingId);
                offersData.push({
                  ...offerData,
                  filesCount
                });
              }
            } catch (listingError) {
              console.error('Error fetching listing:', listingError);
              offersData.push({
                ...offerData,
                filesCount
              });
            }
          } else {
            offersData.push({
              ...offerData,
              filesCount
            });
          }
        }

        setSubmissions(offersData);
      } catch (err) {
        console.error('Error fetching submissions:', err);
        console.error('Error details:', err instanceof Error ? err.message : String(err));
        setError(`Failed to load your submissions: ${err instanceof Error ? err.message : 'Unknown error'}`);
      } finally {
        setLoading(false);
      }
    };

    fetchMySubmissions();
  }, [currentUser]);

  const formatDate = (date?: Date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (offer: Offer) => {
    if (offer.parsingStatus === 'failed') {
      return <span className="status-badge status-error">⚠️ Processing Failed</span>;
    }
    if (offer.parsingStatus === 'pending') {
      return <span className="status-badge status-pending">⏳ Processing</span>;
    }
    if (typeof offer.ptcScore === 'number') {
      return <span className="status-badge status-scored">✓ Scored</span>;
    }
    return <span className="status-badge status-completed">✓ Submitted</span>;
  };

  const getScoreBadge = (score: number | undefined) => {
    if (!score) return null;
    
    let className = 'score-badge';
    let stars = '★';
    
    if (score >= 90) {
      className += ' score-excellent';
      stars = '★★★★★';
    } else if (score >= 80) {
      className += ' score-good';
      stars = '★★★★';
    } else if (score >= 70) {
      className += ' score-fair';
      stars = '★★★';
    } else if (score >= 60) {
      className += ' score-average';
      stars = '★★';
    } else {
      className += ' score-low';
      stars = '★';
    }
    
    return <span className={className}>{stars} {score.toFixed(0)}</span>;
  };

  if (loading) {
    return (
      <div className="my-submissions-container">
        <div className="loading-state">Loading your submissions...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-submissions-container">
        <div className="error-state">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="my-submissions-container">
      <div className="page-header">
        <h1>My Submissions</h1>
        <p className="page-subtitle">Track all offers you've submitted via shareable links</p>
      </div>

      {submissions.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>No Submissions Yet</h3>
          <p>You haven't submitted any offers via shareable links yet.</p>
          <p className="empty-hint">When you submit offers using a listing agent's shareable link, they will appear here.</p>
        </div>
      ) : (
        <div className="submissions-grid">
          {submissions.map((submission) => (
            <div key={submission.id} className="submission-card">
              <div className="submission-header">
                <div className="property-info">
                  <h3 className="property-address">
                    {submission.listingAddress || 'Property Address Unavailable'}
                  </h3>
                  {submission.listingCity && submission.listingState && (
                    <p className="property-location">
                      {submission.listingCity}, {submission.listingState}
                    </p>
                  )}
                </div>
                {getStatusBadge(submission)}
              </div>

              <div className="submission-details">
                <div className="detail-row">
                  <span className="submission-detail-label">Submitted:</span>
                  <span className="submission-detail-value">{formatDate(submission.submissionDate)}</span>
                </div>

                <div className="detail-row">
                  <span className="submission-detail-label">Submission ID:</span>
                  <span className="submission-detail-value submission-id">{submission.id}</span>
                </div>

                {/* Purchase Price - prefer purchase agreement, fallback to pre-approval */}
                {(submission.combinedOfferData?.purchaseAgreement?.financial?.purchasePrice || 
                  submission.preApprovalData?.loan?.purchasePrice) && (
                  <div className="detail-row">
                    <span className="submission-detail-label">Purchase Price:</span>
                    <span className="submission-detail-value">
                      ${(submission.combinedOfferData?.purchaseAgreement?.financial?.purchasePrice || 
                        submission.preApprovalData?.loan?.purchasePrice)?.toLocaleString()}
                    </span>
                  </div>
                )}

                {/* Closing Date from purchase agreement */}
                {submission.combinedOfferData?.purchaseAgreement?.contingencies?.closingDate && (
                  <div className="detail-row">
                    <span className="submission-detail-label">Closing Date:</span>
                    <span className="submission-detail-value">
                      {submission.combinedOfferData.purchaseAgreement.contingencies.closingDate}
                    </span>
                  </div>
                )}

                {/* Loan Amount */}
                {(submission.combinedOfferData?.purchaseAgreement?.financial?.loanAmount || 
                  submission.preApprovalData?.loan?.loanAmount) && (
                  <div className="detail-row">
                    <span className="submission-detail-label">Loan Amount:</span>
                    <span className="submission-detail-value">
                      ${(submission.combinedOfferData?.purchaseAgreement?.financial?.loanAmount || 
                        submission.preApprovalData?.loan?.loanAmount)?.toLocaleString()}
                    </span>
                  </div>
                )}

                {submission.preApprovalData?.lender?.name && (
                  <div className="detail-row">
                    <span className="submission-detail-label">Lender:</span>
                    <span className="submission-detail-value">{submission.preApprovalData.lender.name}</span>
                  </div>
                )}

                {typeof submission.ptcScore === 'number' && (
                  <div className="detail-row score-row">
                    <span className="submission-detail-label">Score:</span>
                    {getScoreBadge(submission.ptcScore)}
                  </div>
                )}

                {submission.parsingStatus === 'failed' && submission.parsingError && (
                  <div className="error-message-box">
                    <strong>⚠️ Processing Error:</strong>
                    <p>{submission.parsingError}</p>
                  </div>
                )}
              </div>

              <div className="submission-files">
                <div className="files-summary">
                  <span>📄 {submission.filesCount || 0} Document{(submission.filesCount || 0) !== 1 ? 's' : ''}</span>
                  {submission.combinedOfferData?.preApproval?.hasData && (
                    <span className="data-badge">✓ Pre-Approval</span>
                  )}
                  {submission.combinedOfferData?.purchaseAgreement?.hasData && (
                    <span className="data-badge">✓ Purchase Agreement</span>
                  )}
                </div>
              </div>

              <div className="submission-actions">
                <button
                  className="btn-view-details"
                  onClick={() => navigate(`/dashboard/offers/${submission.id}`)}
                >
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MySubmissions;
